import type { paths } from "./api";

export type User = paths["/v1/users/me"]["get"]["responses"]["200"]["content"]["application/json"];

export type Role = User["role"];

export type Organization =
  paths["/v1/organizations"]["get"]["responses"]["200"]["content"]["application/json"][number];

export type Participant =
  paths["/v1/participants"]["get"]["responses"]["200"]["content"]["application/json"][number];

export type Tenant =
  paths["/v1/tenants"]["get"]["responses"]["200"]["content"]["application/json"][number];

export type TenantPostBodyParams =
  paths["/v1/tenants"]["post"]["requestBody"]["content"]["application/json"];

export type Configurations =
  paths["/v1/configurations/{id}"]["patch"]["responses"]["200"]["content"]["application/json"];

export type TokenEndpointAuthMethod = NonNullable<
  Configurations["config"]["tokenSetConfig"]
>["tokenEndpointAuthMethod"];

export const TOKEN_ENDPOINT_AUTH_METHODS = [
  "client_secret_basic",
  "client_secret_jwt",
  "client_secret_post",
  "private_key_jwt",
  "self_signed_tls_client_auth",
  "tls_client_auth",
  "none",
] as const satisfies TokenEndpointAuthMethod[];

export type ConfigurationsPatchBody = NonNullable<
  paths["/v1/configurations/{id}"]["patch"]["requestBody"]
>["content"]["application/json"];

export type Payment =
  paths["/v1/payments"]["get"]["responses"]["200"]["content"]["application/json"]["data"][number];

export type PaymentDetails =
  paths["/v1/payments/{tenantId}/{paymentId}"]["get"]["responses"]["200"]["content"]["application/json"];

export type PaymentsQuery = paths["/v1/payments"]["get"]["parameters"]["query"];

export type PaymentStatus = Required<NonNullable<Payment["status"]>>;

export const PAYMENT_STATUSES: PaymentStatus[] = [
  "ACCP",
  "ACPD",
  "ACSC",
  "CANC",
  "PDNG",
  "RCVD",
  "RJCT",
  "SCHD",
] as const;

export type Consent =
  paths["/v1/consents"]["get"]["responses"]["200"]["content"]["application/json"]["data"][number];

export type ConsentWithOrgName =
  paths["/v1/consents/{tenantId}/{consentId}"]["get"]["responses"]["200"]["content"]["application/json"]["consent"];

export type ConsentStatusHistory =
  paths["/v1/consents/{tenantId}/{consentId}"]["get"]["responses"]["200"]["content"]["application/json"]["statusHistory"];

export type ConsentsQuery = paths["/v1/consents"]["get"]["parameters"]["query"];

export const CONSENT_PRODUCT_TYPES: Required<Consent["productType"][]> = [
  "CUSTOMER_DATA",
  "INSTANT_SCHEDULED_PAYMENT",
  "AUTOMATIC_PAYMENT",
  "ENROLLMENT",
] as const;

export type ConsentProductType = Exclude<Consent["productType"], undefined>;

export const CONSENT_STATUSES: Required<
  paths["/v1/consents"]["get"]["parameters"]["query"]["status"]
> = [
  "AUTHORISED",
  "AWAITING_AUTHORISATION",
  "AWAITING_ACCOUNT_HOLDER_VALIDATION",
  "AWAITING_RISK_SIGNALS",
  "AWAITING_ENROLLMENT",
  "REJECTED",
  "PARTIALLY_ACCEPTED",
  "REVOKED",
  "CONSUMED",
] as const;
export type ConsentStatus = Exclude<ConsentsQuery["status"], undefined>[number];

export const PAYMENT_CONSENT_STATUSES = [
  "AUTHORISED",
  "AWAITING_AUTHORISATION",
  "REJECTED",
  "PARTIALLY_ACCEPTED",
  "CONSUMED",
] as const;
export type PaymentConsentStatus = (typeof PAYMENT_CONSENT_STATUSES)[number];

export const DATA_CONSENT_STATUSES = ["AUTHORISED", "AWAITING_AUTHORISATION", "REJECTED"] as const;
export type DataConsentStatus = (typeof DATA_CONSENT_STATUSES)[number];

export const AUTOMATIC_PAYMENT_CONSENT_STATUSES = [
  "AUTHORISED",
  "AWAITING_AUTHORISATION",
  "REJECTED",
  "PARTIALLY_ACCEPTED",
  "REVOKED",
  "CONSUMED",
] as const;
export type AutomaticPaymentConsentStatus = (typeof AUTOMATIC_PAYMENT_CONSENT_STATUSES)[number];

export const ENROLLMENT_STATUSES = [
  "AUTHORISED",
  "AWAITING_ACCOUNT_HOLDER_VALIDATION",
  "AWAITING_RISK_SIGNALS",
  "AWAITING_ENROLLMENT",
  "REJECTED",
  "REVOKED",
] as const;
export type EnrollmentStatus = (typeof ENROLLMENT_STATUSES)[number];

export type InternalCredentials =
  paths["/v1/internal/credentials/{tenantId}"]["get"]["responses"]["200"]["content"]["application/json"][number];

export type InternalCredentialsPostBodyResponse =
  paths["/v1/internal/credentials/{tenantId}"]["post"]["responses"]["201"]["content"]["application/json"];

export type FeatureFlag =
  paths["/v1/feature-flags/{tenantId}"]["get"]["responses"]["200"]["content"]["application/json"][number];

export type Client =
  paths["/v1/clients/{tenantId}"]["get"]["responses"]["200"]["content"]["application/json"]["data"][number];

export type ClientDetails =
  paths["/v1/clients/{tenantId}/{clientId}"]["get"]["responses"]["200"]["content"]["application/json"];
export type PcmPrivateReport =
  paths["/v1/pcm/private/reports"]["get"]["responses"]["200"]["content"]["application/json"]["data"][number];

export type PcmPrivateReportStatus = PcmPrivateReport["status"];

export type PcmHybridFlowReport =
  paths["/v1/pcm/hybrid-flow/reports"]["get"]["responses"]["200"]["content"]["application/json"]["data"][number];

export type PcmHybridFlowEvent = PcmHybridFlowReport["event"];

export type PcmOpendataReport =
  paths["/v1/pcm/opendata/reports"]["get"]["responses"]["200"]["content"]["application/json"]["data"][number];

export type PcmOpendataReportStatus = PcmOpendataReport["status"];

export type UserActivity =
  paths["/v1/user-activities"]["get"]["responses"]["200"]["content"]["application/json"]["data"][number];

export type UserActivityType = UserActivity["activityType"];

export const ACTIVITY_TYPES = [
  "auth",
  "featureFlags",
  "internalCredentials",
  "organizations",
  "tenants",
  "users",
] as const satisfies UserActivityType[];
